import * as Select from '@radix-ui/react-select';
import type { CSSProperties, ReactNode } from 'react';
import React, { useState, useEffect, useRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faTimes,
  faCheck,
  faSpinnerThird,
} from '@fortawesome/pro-solid-svg-icons';
import styles from './dropdown-input.module.scss';
import { colors } from '@/Themes/Colors';
import useScreenType from '@/hooks/util/useScreenType';

/**
 * Opção exibida pelo {@link DropdownInput}.
 */
export interface IDropdownInputOptions {
  /** Identificador único da opção. */
  key: string;
  /** Texto exibido para o usuário. */
  text: string;
  /** Valor retornado em `onChange` ao selecionar a opção. */
  value: string;
  /** Desabilita a opção na lista. */
  disabled?: boolean;
}

/**
 * Forma de exibir os valores na seleção múltipla do {@link DropdownInput}.
 *
 * - `summary` — texto resumido (`1 selecionado` / `N selecionados`)
 * - `tags` — chips removíveis dentro do campo
 */
export type DropdownInputSelectionDisplay = 'summary' | 'tags';

export interface DropdownInputProps {
  /** Nome do campo. Usado como `id` do input/trigger. */
  name?: string;
  /** Label exibido acima do dropdown. */
  label?: ReactNode;
  /**
   * Opções disponíveis para seleção.
   *
   * Opcional quando `allowCustomValues` está ativo, pois o campo passa a
   * funcionar como input de texto livre.
   * @default []
   */
  options?: IDropdownInputOptions[];
  /**
   * Valor controlado.
   * - seleção única: `string`
   * - seleção múltipla / valores livres: `string[]`
   */
  value?: string | string[];
  /** Valor inicial não controlado. */
  defaultValue?: string | string[];
  /**
   * Texto exibido quando nenhum valor está selecionado.
   * @default 'Selecione uma opção'
   */
  placeholder?: string;
  /** Desabilita o dropdown. */
  disabled?: boolean;
  /**
   * Exibe o indicador visual de obrigatoriedade e aplica `aria-required`.
   *
   * A validação fica a cargo do formulário: o `required` nativo iria para o
   * select oculto do Radix, que bloqueia o submit sem conseguir exibir a
   * mensagem do navegador por não ser focável.
   */
  required?: boolean;
  /** Força o estado visual de erro. */
  error?: boolean;
  /** Mensagem de erro exibida abaixo do campo. Também ativa o estado de erro. */
  errorMessage?: ReactNode;
  /**
   * Mensagem exibida quando a busca não encontra resultados.
   * @default 'Nenhum resultado encontrado'
   */
  noResultsMessage?: string;
  /** Classe CSS do container principal. */
  containerClassName?: string;
  /** Estilo inline do container principal. */
  containerStyle?: CSSProperties;
  /** Classe CSS da mensagem de erro. */
  errorClassName?: string;
  /** Estilo inline da mensagem de erro. */
  errorStyle?: CSSProperties;
  /** Classe CSS do label. */
  labelClassName?: string;
  /** Estilo inline do label. */
  labelStyle?: CSSProperties;
  /** Classe CSS do trigger do dropdown. */
  className?: string;
  /** Classe CSS adicional do input/trigger. */
  inputClassName?: string;
  /** Estilo inline do input/trigger. */
  inputStyle?: CSSProperties;
  /** Estilo inline do valor exibido no modo `summary`. */
  displayValueStyle?: CSSProperties;
  /** Cor do texto do valor exibido no modo `summary`. */
  displayValueColor?: string;
  /** Cor dos ícones (chevron, loading e clear). */
  iconColor?: string;
  /**
   * Gira o chevron quando o dropdown abre.
   * @default true
   */
  rotateIconOnOpen?: boolean;
  /**
   * Quando `true`, não atualiza o valor exibido após a seleção.
   * Útil para menus de ação.
   * @default false
   */
  disableSelection?: boolean;
  /**
   * Habilita seleção múltipla com checkboxes.
   * @default false
   */
  multiple?: boolean;
  /**
   * Define como os valores da seleção múltipla são exibidos.
   *
   * - `summary` — comportamento legado (`N selecionados`)
   * - `tags` — chips removíveis no corpo do campo
   *
   * Também é ativado implicitamente por `allowCustomValues` e `allowCsvImport`.
   * @default 'summary'
   */
  selectionDisplay?: DropdownInputSelectionDisplay;
  /**
   * Permite adicionar valores livres com `Enter` ou ao colar texto com quebras
   * de linha. Nesse modo o dropdown não exibe a lista de opções e funciona
   * como um input de texto com chips.
   * @default false
   */
  allowCustomValues?: boolean;
  /**
   * Exibe a ação "Importar arquivo CSV" ao lado do label.
   * O nome do arquivo importado aparece como chip no campo.
   * @default false
   */
  allowCsvImport?: boolean;
  /**
   * Texto da ação de importação de CSV.
   * @default 'Importar arquivo CSV'
   */
  csvImportLabel?: string;
  /**
   * Callback chamado ao importar ou remover um arquivo CSV.
   * Recebe `null` quando o arquivo é removido.
   */
  onCsvImport?: (file: File | null) => void;
  /**
   * Exibe o botão de limpar seleção.
   * @default false
   */
  clearable?: boolean;
  /**
   * Exibe indicador de carregamento e desabilita a interação.
   * @default false
   */
  loading?: boolean;
  /**
   * Habilita o campo de busca dentro do dropdown.
   * @default false
   */
  search?: boolean;
  /**
   * Placeholder do campo de busca.
   * @default 'Buscar...'
   */
  searchPlaceholder?: string;
  /**
   * Callback chamado quando o valor é alterado.
   * Retorna `string` na seleção única e `string[]` na múltipla.
   */
  onChange?: (value: string | string[]) => void;
  /** Callback chamado quando o termo de busca é alterado. */
  onSearchChange?: (searchTerm: string) => void;
  /** ID para testes automatizados. */
  'data-testid'?: string;
}

/**
 * Dropdown baseado em `@radix-ui/react-select`, com suporte a seleção única,
 * múltipla, busca e modos de entrada por chips.
 *
 * Em `multiple`, o valor pode ser exibido como resumo (`selectionDisplay="summary"`)
 * ou como chips removíveis (`selectionDisplay="tags"`).
 *
 * Com `allowCustomValues`, o campo deixa de abrir opções e passa a funcionar
 * como input de texto: valores são adicionados com `Enter` ou ao colar linhas.
 *
 * Com `allowCsvImport`, uma ação de importação é exibida e o nome do arquivo
 * aparece como chip no campo. Ao importar um CSV no modo `allowCustomValues`,
 * os valores digitados são limpos e a digitação fica bloqueada enquanto o
 * arquivo estiver presente.
 *
 * @example
 * // Seleção múltipla com chips
 * <DropdownInput
 *   multiple
 *   selectionDisplay="tags"
 *   label="Municípios"
 *   options={cities}
 *   value={selectedCities}
 *   onChange={setSelectedCities}
 * />
 *
 * @example
 * // Valores livres (sem lista de opções)
 * <DropdownInput
 *   multiple
 *   allowCustomValues
 *   label="Entregadores"
 *   placeholder="Digite o ID dos entregadores."
 *   value={deliverymenIds}
 *   onChange={setDeliverymenIds}
 * />
 *
 * @example
 * // Importação de CSV
 * <DropdownInput
 *   multiple
 *   allowCustomValues
 *   allowCsvImport
 *   label="Entregadores"
 *   onCsvImport={setCsvFile}
 *   value={deliverymenIds}
 *   onChange={setDeliverymenIds}
 * />
 */
export const DropdownInput = ({
  name,
  label,
  options = [],
  value,
  defaultValue,
  placeholder = 'Selecione uma opção',
  disabled,
  required,
  error,
  errorMessage,
  noResultsMessage = 'Nenhum resultado encontrado',
  containerClassName,
  containerStyle,
  errorClassName,
  errorStyle,
  labelClassName,
  labelStyle,
  className,
  inputClassName,
  inputStyle,
  displayValueStyle,
  displayValueColor,
  iconColor,
  rotateIconOnOpen = true,
  disableSelection = false,
  multiple = false,
  selectionDisplay = 'summary',
  allowCustomValues = false,
  allowCsvImport = false,
  csvImportLabel = 'Importar arquivo CSV',
  onCsvImport,
  clearable = false,
  loading = false,
  search = false,
  searchPlaceholder = 'Buscar...',
  onChange,
  onSearchChange,
  'data-testid': dataTestId,
}: DropdownInputProps) => {
  const isMobile = useScreenType('mobile');
  const isTablet = useScreenType('tablet');
  const isError = error || !!errorMessage;
  const [selectedValues, setSelectedValues] = useState<string[]>(
    multiple ? (Array.isArray(value) ? value : value ? [value] : []) : [],
  );
  const [singleValue, setSingleValue] = useState<string | undefined>(
    !multiple && typeof value === 'string' ? value : undefined,
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [customValue, setCustomValue] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const isClosingBySelection = useRef<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const singleSearchInputRef = useRef<HTMLInputElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (multiple) {
      setSelectedValues(Array.isArray(value) ? value : value ? [value] : []);
      return;
    }

    setSingleValue(typeof value === 'string' ? value : undefined);
  }, [value, multiple]);

  useEffect(() => {
    if (!(isMobile || isTablet) || !isOpen || !search) return;

    const handleViewportChange = () => {
      if (isSearchFocused) {
        setIsOpen(true);
      }
    };

    const handleResize = () => handleViewportChange();
    const handleOrientationChange = () => handleViewportChange();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobile, isTablet, isOpen, search, isSearchFocused]);

  const handleClear = () => {
    if (multiple) {
      setSelectedValues([]);
      onChange?.([]);
      return;
    }

    setSingleValue(undefined);
    onChange?.('');
  };

  const handleMultipleChange = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const addCustomValues = (rawValues: string[]) => {
    const valuesToAdd = rawValues
      .map(item => item.trim())
      .filter(
        (item, index, values) =>
          item &&
          !selectedValues.includes(item) &&
          values.indexOf(item) === index,
      );

    if (valuesToAdd.length === 0) return;

    const newValues = [...selectedValues, ...valuesToAdd];
    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const removeSelectedValue = (optionValue: string) => {
    const newValues = selectedValues.filter(value => value !== optionValue);
    setSelectedValues(newValues);
    onChange?.(newValues);
  };

  const getOptionText = (optionValue: string) =>
    options.find(option => option.value === optionValue)?.text || optionValue;

  const renderTag = (
    optionValue: string,
    remove: () => void,
    interactiveElement = false,
  ) => {
    const TagElement = interactiveElement ? 'button' : 'span';
    const canRemove = !disabled && !loading;

    return (
      <span className={styles.tag} key={optionValue}>
        <span className={styles.tagText}>{getOptionText(optionValue)}</span>
        {canRemove ? (
          <TagElement
            {...(interactiveElement ? { type: 'button' as const } : {})}
            className={styles.tagRemove}
            role={interactiveElement ? undefined : 'button'}
            tabIndex={interactiveElement ? undefined : -1}
            aria-label={`Remover ${getOptionText(optionValue)}`}
            onPointerDown={event => {
              event.stopPropagation();
            }}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              remove();
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </TagElement>
        ) : null}
      </span>
    );
  };

  const renderFileTag = (interactiveElement = false) =>
    selectedFile
      ? renderTag(
          selectedFile.name,
          () => {
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            onCsvImport?.(null);
          },
          interactiveElement,
        )
      : null;

  const renderLabel = () => {
    if (!label && !allowCsvImport) return null;

    if (!allowCsvImport) {
      return (
        <label
          htmlFor={name}
          className={`${styles.label} ${labelClassName || ''}`}
          style={labelStyle}
        >
          {label}
          {required && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
      );
    }

    return (
      <div className={styles.labelRow}>
        {label ? (
          <label
            htmlFor={name}
            className={`${styles.label} ${labelClassName || ''}`}
            style={labelStyle}
          >
            {label}
            {required && (
              <span className={styles.requiredIndicator} aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </label>
        ) : (
          <span />
        )}
        {allowCsvImport && (
          <>
            <button
              type="button"
              className={styles.csvImportButton}
              disabled={disabled || loading}
              onClick={() => fileInputRef.current?.click()}
            >
              {csvImportLabel}
            </button>
            <input
              ref={fileInputRef}
              className={styles.hiddenFileInput}
              type="file"
              accept=".csv,text/csv"
              data-testid={dataTestId ? `${dataTestId}-csv-input` : undefined}
              onChange={event => {
                const file = event.target.files?.[0];
                if (!file) return;

                setSelectedFile(file);
                if (multiple && allowCustomValues && selectedValues.length) {
                  setSelectedValues([]);
                  onChange?.([]);
                }
                onCsvImport?.(file);
              }}
            />
          </>
        )}
      </div>
    );
  };

  const getDisplayValue = () => {
    if (loading) return 'Carregando...';

    if (multiple) {
      if (selectedValues.length === 0) return placeholder;

      if (selectedValues.length === 1) {
        const option = options.find(o => o.value === selectedValues[0]);
        return option?.text || placeholder;
      }

      return `${selectedValues.length} selecionados`;
    }

    if (singleValue) {
      const option = options.find(o => o.value === singleValue);

      return option?.text || singleValue || placeholder;
    }

    return placeholder;
  };

  const showClearButton =
    clearable &&
    ((multiple && selectedValues.length > 0) || (!multiple && singleValue));

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    onSearchChange?.(newSearchTerm);
    setFocusedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    const availableOptions = filteredOptions.filter(option => !option.disabled);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = prev < availableOptions.length - 1 ? prev + 1 : 0;
          setTimeout(() => {
            const focusedElement = viewportRef.current?.querySelector(
              `.${styles.focused}`,
            );
            if (focusedElement) {
              focusedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
          return nextIndex;
        });
        break;

      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(prev => {
          const nextIndex = prev > 0 ? prev - 1 : availableOptions.length - 1;
          setTimeout(() => {
            const focusedElement = viewportRef.current?.querySelector(
              `.${styles.focused}`,
            );
            if (focusedElement) {
              focusedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
          return nextIndex;
        });
        break;

      case 'Enter':
        event.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < availableOptions.length) {
          const selectedOption = availableOptions[focusedIndex];
          if (multiple) {
            handleMultipleChange(selectedOption.value);
          } else {
            setSingleValue(selectedOption.value);
            onChange?.(selectedOption.value);
            isClosingBySelection.current = true;
            setIsOpen(false);
            setFocusedIndex(-1);
            setTimeout(() => {
              setSearchTerm('');
            }, 0);
          }
        } else if (availableOptions.length > 0) {
          const firstOption = availableOptions[0];
          if (multiple) {
            handleMultipleChange(firstOption.value);
          } else {
            setSingleValue(firstOption.value);
            onChange?.(firstOption.value);
            isClosingBySelection.current = true;
            setIsOpen(false);
            setFocusedIndex(-1);
            setTimeout(() => {
              setSearchTerm('');
            }, 0);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  const filteredOptions = search
    ? options.filter(option =>
        option.text.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : options;

  const isClearDisabled = disabled || loading;
  const showTags =
    selectionDisplay === 'tags' || allowCustomValues || allowCsvImport;
  const hasVisibleTags =
    showTags && (selectedValues.length > 0 || !!selectedFile);

  if (multiple && allowCustomValues) {
    return (
      <div
        className={`${styles.Dropdown} ${containerClassName || ''} ${
          disabled ? styles.disabled : ''
        } ${isError ? styles.error : ''}`}
        style={containerStyle}
      >
        {renderLabel()}

        <div
          className={`${styles.customValuesInput} ${
            selectedValues.length || selectedFile ? styles.hasValue : ''
          } ${inputClassName || ''}`}
          style={inputStyle}
          onClick={() => searchInputRef.current?.focus()}
        >
          {selectedValues.map(selectedValue =>
            renderTag(
              selectedValue,
              () => removeSelectedValue(selectedValue),
              true,
            ),
          )}
          {renderFileTag(true)}
          <input
            ref={searchInputRef}
            id={name}
            data-testid={dataTestId}
            className={styles.customValueTextInput}
            type="text"
            value={customValue}
            placeholder={
              selectedValues.length || selectedFile ? undefined : placeholder
            }
            disabled={disabled || loading || !!selectedFile}
            required={required}
            onChange={event => setCustomValue(event.target.value)}
            onPaste={event => {
              if (selectedFile) return;

              const pastedValue = event.clipboardData.getData('text');
              if (!/[\r\n]/.test(pastedValue)) return;

              event.preventDefault();
              addCustomValues(pastedValue.split(/\r?\n/));
              setCustomValue('');
            }}
            onKeyDown={event => {
              if (selectedFile) return;

              if (event.key === 'Enter') {
                event.preventDefault();
                addCustomValues([customValue]);
                setCustomValue('');
              }

              if (
                event.key === 'Backspace' &&
                !customValue &&
                selectedValues.length
              ) {
                removeSelectedValue(selectedValues[selectedValues.length - 1]);
              }
            }}
          />
          {loading && (
            <FontAwesomeIcon
              icon={faSpinnerThird}
              spin
              color={iconColor || colors.gray10}
            />
          )}
        </div>

        {errorMessage && (
          <span
            className={`${styles.errorMessage} ${errorClassName || ''}`}
            style={errorStyle}
          >
            {errorMessage}
          </span>
        )}
      </div>
    );
  }

  if (multiple) {
    return (
      <div
        className={`${styles.Dropdown} ${containerClassName || ''} ${
          disabled ? styles.disabled : ''
        } ${isError ? styles.error : ''}`}
        style={containerStyle}
      >
        {renderLabel()}

        <div className={styles.triggerWrapper} onKeyDown={handleKeyDown}>
          <Select.Root
            value={selectedValues[0]}
            onValueChange={() => {}}
            disabled={disabled || loading}
            name={name}
            open={isOpen}
            onOpenChange={open => {
              if (
                !open &&
                (isMobile || isTablet) &&
                isSearchFocused &&
                search
              ) {
                return;
              }

              setIsOpen(open);

              if (open && search) {
                if (!isMobile && !isTablet) {
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 100);
                }
                return;
              }

              if (!open && search) {
                setSearchTerm('');
                if (selectedValues.length === 0) {
                  onSearchChange?.('');
                }
                setIsSearchFocused(false);
              }
            }}
          >
            <Select.Trigger
              className={`${styles.trigger} ${className || ''} ${
                selectedValues.length > 0 && !searchTerm ? styles.hasValue : ''
              } ${hasVisibleTags ? styles.withTags : ''} ${
                searchTerm ? styles.searching : ''
              } ${inputClassName || ''}`}
              id={name}
              data-testid={dataTestId}
              aria-required={required}
              onKeyDown={handleKeyDown}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              style={inputStyle}
            >
              {hasVisibleTags ? (
                <span className={styles.tags}>
                  {selectedValues.map(selectedValue =>
                    renderTag(selectedValue, () =>
                      removeSelectedValue(selectedValue),
                    ),
                  )}
                  {renderFileTag()}
                </span>
              ) : (
                <span
                  className={styles.displayValue}
                  style={{
                    ...displayValueStyle,
                    color: displayValueColor,
                  }}
                >
                  {getDisplayValue()}
                </span>
              )}
              <div className={styles.icons}>
                {showClearButton && (
                  <FontAwesomeIcon
                    color={iconColor || colors.gray10}
                    onPointerDown={e => {
                      if (isClearDisabled) return;
                      e.stopPropagation();
                    }}
                    onMouseDown={e => {
                      if (isClearDisabled) return;

                      e.stopPropagation();
                    }}
                    onClick={e => {
                      if (isClearDisabled) return;

                      e.preventDefault();
                      e.stopPropagation();
                      handleClear();
                    }}
                    icon={faTimes}
                  />
                )}
                <Select.Icon
                  className={`${styles.icon} ${
                    rotateIconOnOpen ? '' : styles.staticIcon
                  }`}
                >
                  {loading ? (
                    <FontAwesomeIcon
                      icon={faSpinnerThird}
                      spin
                      color={iconColor || colors.gray10}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      color={iconColor || colors.gray10}
                    />
                  )}
                </Select.Icon>
              </div>
            </Select.Trigger>

            <Select.Portal>
              <Select.Content className={styles.content} position="popper">
                <Select.Viewport className={styles.viewport} ref={viewportRef}>
                  {search && (
                    <div className={styles.searchWrapper}>
                      <input
                        ref={searchInputRef}
                        className={styles.searchInput}
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={e => handleSearchChange(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        onFocus={e => {
                          e.stopPropagation();
                          setIsSearchFocused(true);
                        }}
                        onBlur={() => {
                          setIsSearchFocused(false);
                        }}
                        onKeyDown={e => e.stopPropagation()}
                        onPointerDown={e => e.stopPropagation()}
                      />
                    </div>
                  )}
                  {filteredOptions.length === 0 ? (
                    <div className={styles.noResults}>{noResultsMessage}</div>
                  ) : (
                    filteredOptions.map(option => {
                      const availableOptions = filteredOptions.filter(
                        opt => !opt.disabled,
                      );
                      const availableIndex = availableOptions.findIndex(
                        opt => opt.value === option.value,
                      );
                      const isFocused =
                        availableIndex === focusedIndex && !option.disabled;

                      return (
                        <div
                          key={option.key}
                          className={`${styles.multipleItem} ${
                            selectedValues.includes(option.value)
                              ? styles.selected
                              : ''
                          } ${option.disabled ? styles.itemDisabled : ''} ${
                            isFocused ? styles.focused : ''
                          }`}
                          onClick={e => {
                            e.stopPropagation();
                            if (option.disabled) return;
                            handleMultipleChange(option.value);
                          }}
                          onMouseDown={e => e.stopPropagation()}
                          onMouseEnter={() => {
                            if (!option.disabled) {
                              setFocusedIndex(availableIndex);
                            }
                          }}
                        >
                          <div className={styles.checkbox}>
                            {selectedValues.includes(option.value) && (
                              <FontAwesomeIcon icon={faCheck} />
                            )}
                          </div>
                          <span>{option.text}</span>
                        </div>
                      );
                    })
                  )}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>

        {errorMessage && (
          <span
            className={`${styles.errorMessage} ${errorClassName || ''}`}
            style={errorStyle}
          >
            {errorMessage}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.Dropdown} ${containerClassName || ''} ${
        disabled ? styles.disabled : ''
      } ${isError ? styles.error : ''}`}
      style={containerStyle}
    >
      {renderLabel()}

      <div className={styles.triggerWrapper} onKeyDown={handleKeyDown}>
        <Select.Root
          value={singleValue}
          defaultValue={
            typeof defaultValue === 'string' ? defaultValue : undefined
          }
          onValueChange={() => {}}
          disabled={disabled || loading}
          name={name}
          open={isOpen}
          onOpenChange={open => {
            if (!open && (isMobile || isTablet) && isSearchFocused && search) {
              return;
            }

            setIsOpen(open);

            if (open && search) {
              if (!isMobile && !isTablet) {
                setTimeout(() => {
                  singleSearchInputRef.current?.focus();
                }, 100);
              }
              return;
            }

            if (!open && search) {
              setSearchTerm('');
              if (!isClosingBySelection.current && !singleValue) {
                onSearchChange?.('');
              }
              isClosingBySelection.current = false;
              setIsSearchFocused(false);
            }
          }}
        >
          <Select.Trigger
            className={`${styles.trigger} ${className || ''} ${
              singleValue && !searchTerm ? styles.hasValue : ''
            } ${searchTerm ? styles.searching : ''} ${inputClassName || ''}`}
            id={name}
            data-testid={dataTestId}
            aria-required={required}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            style={inputStyle}
          >
            <span
              className={styles.displayValue}
              style={{
                ...displayValueStyle,
                color: displayValueColor,
              }}
            >
              {getDisplayValue()}
            </span>
            <div className={styles.icons}>
              {showClearButton && (
                <FontAwesomeIcon
                  color={iconColor || colors.gray10}
                  onPointerDown={e => {
                    if (isClearDisabled) return;
                    e.stopPropagation();
                  }}
                  onMouseDown={e => {
                    if (isClearDisabled) return;

                    e.stopPropagation();
                  }}
                  onClick={e => {
                    if (isClearDisabled) return;

                    e.preventDefault();
                    e.stopPropagation();
                    handleClear();
                  }}
                  icon={faTimes}
                />
              )}
              <Select.Icon
                className={`${styles.icon} ${
                  rotateIconOnOpen ? '' : styles.staticIcon
                }`}
              >
                {loading ? (
                  <FontAwesomeIcon
                    icon={faSpinnerThird}
                    spin
                    color={iconColor || colors.gray10}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    color={iconColor || colors.gray10}
                  />
                )}
              </Select.Icon>
            </div>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content className={styles.content} position="popper">
              <Select.Viewport className={styles.viewport} ref={viewportRef}>
                {search && (
                  <div className={styles.searchWrapper}>
                    <input
                      ref={singleSearchInputRef}
                      className={styles.searchInput}
                      type="text"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={e => handleSearchChange(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onTouchStart={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                      onFocus={e => {
                        e.stopPropagation();
                        setIsSearchFocused(true);
                      }}
                      onBlur={() => {
                        setIsSearchFocused(false);
                      }}
                      onKeyDown={e => e.stopPropagation()}
                      onPointerDown={e => e.stopPropagation()}
                    />
                  </div>
                )}
                {filteredOptions.length === 0 ? (
                  <div className={styles.noResults}>{noResultsMessage}</div>
                ) : (
                  filteredOptions.map(option => {
                    const availableOptions = filteredOptions.filter(
                      opt => !opt.disabled,
                    );
                    const availableIndex = availableOptions.findIndex(
                      opt => opt.value === option.value,
                    );
                    const isFocused =
                      availableIndex === focusedIndex && !option.disabled;

                    return (
                      <div
                        key={option.key}
                        className={`${styles.item} ${
                          singleValue === option.value ? styles.selected : ''
                        } ${option.disabled ? styles.itemDisabled : ''} ${
                          isFocused ? styles.focused : ''
                        }`}
                        onClick={e => {
                          e.stopPropagation();
                          if (option.disabled) return;

                          if (!disableSelection) {
                            setSingleValue(option.value);
                          }
                          onChange?.(option.value);
                          isClosingBySelection.current = true;
                          setIsOpen(false);
                          setFocusedIndex(-1);
                          setTimeout(() => {
                            setSearchTerm('');
                          }, 0);
                        }}
                        onMouseDown={e => e.stopPropagation()}
                        onMouseEnter={() => {
                          if (!option.disabled) {
                            setFocusedIndex(availableIndex);
                          }
                        }}
                      >
                        <span>{option.text}</span>
                      </div>
                    );
                  })
                )}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>

      {errorMessage && (
        <span
          className={`${styles.errorMessage} ${errorClassName || ''}`}
          style={errorStyle}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
};
